"use client";

import BundleContextStrip from "src/components/bundles/BundleContextStrip";
import React, { useCallback, useState } from "react";
import NextLink from "next/link";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { CanvasVariant } from "./variant-resolver";
import { formatMoney, variantDisplayPrice } from "./variant-price";
import { entryVariant, sizeLabel } from "./size-groups";
import { variantsOfFinish, type Finish } from "./finish-options";

/**
 * The dropzone's dashed outline, drawn as SVG rather than a CSS border.
 *
 * `border: dashed` ties dash length to border width — the only way to get
 * longer dashes is a thicker, clumsier stroke. An SVG rect sets the two
 * independently, so the outline can be bold and clearly dashed without
 * turning into a heavy frame. Half the stroke falls outside the viewport and
 * is clipped, hence a width of 6 to leave 3 visible.
 */
const dashedOutline = (color: string) =>
  `url("data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='100%' height='100%'>` +
      `<rect width='100%' height='100%' rx='12' ry='12' fill='none' ` +
      `stroke='${color}' stroke-width='6' stroke-dasharray='20 14' />` +
      `</svg>`,
  )}")`;

export interface CanvasUploadPanelProps {
  /** The catalogue name ("Povečave"). Kept as the fallback headline. */
  productName: string;
  /** Where the ready-made collections live. Omitted, the link is not shown. */
  galleryHref?: string;
  variants: CanvasVariant[];
  currency: string;
  locale: string;
  uploading: boolean;
  onFile: (file: File) => void;
  t: (key: string) => string;
  /** A ?material= link's finish: canvas gets its own headline and entry price. */
  preferredFinish?: Finish;
}

/**
 * The first screen: one job, done properly.
 *
 * Before this, the page opened on a heading and a button floating in white
 * space, which said nothing about what was being bought or what it costs. The
 * panel keeps the single action but gives it the context a product page owes
 * the customer — what this is, what it starts at, and how soon it arrives —
 * and accepts a dropped file, which is how most people arrive with a photo
 * already in hand.
 */
export default function CanvasUploadPanel({
  productName,
  galleryHref,
  variants,
  currency,
  locale,
  uploading,
  onFile,
  t,
  preferredFinish,
}: CanvasUploadPanelProps) {
  const [dragging, setDragging] = useState(false);

  // What the headline price quotes: the entry enlargement (40 × 60), the same
  // variant the designer opens on. The catalogue's cheapest item is a 20 × 20
  // square — a true "from" price, but a poor promise under a heading about
  // large photographs.
  // From the "Na platnu" door the promise is canvas: its cheapest stretched
  // size (30 × 40), not the paper 40 × 60 the default page quotes.
  const isCanvas = preferredFinish?.material === "canvas";
  const canvasEntry = isCanvas
    ? [...variantsOfFinish(variants, preferredFinish)].sort(
        (a, b) =>
          variantDisplayPrice(a, currency).amount -
          variantDisplayPrice(b, currency).amount,
      )[0]
    : undefined;
  const entry = canvasEntry ?? entryVariant(variants, currency);

  // The market's own amount from priceCurrencies, not the euro base under the
  // local symbol — and formatted in the customer's locale, not Slovenian.
  const cheapest = entry ? variantDisplayPrice(entry, currency) : null;

  // The size that "from" price buys, read off the same variant as the price.
  const cheapestSize = entry?.printSpec
    ? sizeLabel(entry.printSpec.widthMm, entry.printSpec.heightMm)
    : "";

  // The headline is the promise, not the SKU: "Povečave" names a catalogue
  // shelf, while the customer arrives wanting a big print of their photo.
  const headline =
    (isCanvas ? t("canvasHeroTitleCanvas") : "") || t("canvasHeroTitle") || productName;

  const take = useCallback(
    (files: FileList | null | undefined) => {
      const file = files?.[0];
      if (file) onFile(file);
    },
    [onFile],
  );

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 4, md: 8 } }}>
      {/* "Za paket »X«" after a paket slot link; nothing otherwise. */}
      <BundleContextStrip sx={{ mb: 2 }} />
      <Stack
        spacing={2}
        alignItems="center"
        textAlign="center"
        sx={(theme) => ({
          px: { xs: 2, md: 4 },
          py: { xs: 3, md: 4 },
          borderRadius: 4,
          background: `linear-gradient(160deg, ${alpha(
            theme.palette.primary.main,
            0.1,
          )} 0%, ${alpha(theme.palette.secondary.main, 0.06)} 100%)`,
        })}
      >
        <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: -0.5 }}>
          {headline}
        </Typography>
        {cheapest !== null && (
          // A price the eye lands on. Grey small print under a heading reads as
          // a disclaimer; the same number in the brand colour reads as an offer.
          // One text flow rather than three flex children: side by side they
          // render identically, but only this way does the line survive being
          // copied or read aloud as "Od 19,90 € za 40 × 60 cm" instead of
          // running together into "Od19,90 €za".
          <Typography
            variant="body1"
            sx={(theme) => ({
              px: 2,
              py: 1,
              borderRadius: 999,
              color: theme.palette.primary.main,
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
            })}
          >
            {t("canvasPriceFrom")}{" "}
            <Box component="span" sx={{ fontWeight: 700, fontSize: "1.2em" }}>
              {formatMoney(cheapest.amount, cheapest.currency, locale)}
            </Box>
            {cheapestSize ? ` ${t("canvasPriceForSize")} ${cheapestSize}` : ""}
          </Typography>
        )}
      </Stack>

      <Box
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (!uploading) take(e.dataTransfer?.files);
        }}
        sx={(theme) => ({
          mt: 4,
          px: 3,
          py: { xs: 5, md: 7 },
          borderRadius: 3,
          textAlign: "center",
          // The drop target has to announce itself. Thin grey dashes on white
          // read as a disabled panel or a divider, and a customer who does not
          // recognise this as the place to put their photo has no second clue
          // on the page. Heavier strokes in the brand colour over a faint tint
          // say "here" before anything is read. Dash length scales with border
          // width, so one number widens and lengthens them together.
          backgroundImage: dashedOutline(
            dragging
              ? theme.palette.primary.main
              : alpha(theme.palette.primary.main, 0.6),
          ),
          backgroundColor: dragging
            ? alpha(theme.palette.primary.main, 0.1)
            : alpha(theme.palette.primary.main, 0.03),
          transition: "background-color 150ms",
        })}
      >
        <Stack spacing={2.5} alignItems="center">
          <Typography variant="h6">{t("canvasUploadTitle")}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t("canvasUploadHint")}
          </Typography>

          <Button
            variant="contained"
            size="large"
            component="label"
            disabled={uploading}
            startIcon={
              uploading ? <CircularProgress size={16} color="inherit" /> : undefined
            }
          >
            {uploading ? t("canvasUploading") : t("canvasChoosePhoto")}
            <input
              hidden
              type="file"
              accept="image/*"
              onChange={(e) => take(e.target.files)}
            />
          </Button>
        </Stack>
      </Box>

      <Stack spacing={0.5} sx={{ mt: 3 }} alignItems="center">
        {/* Not everyone arrives with a photo in mind. */}
        {galleryHref ? (
          <Link component={NextLink} href={galleryHref} sx={{ mb: 1, fontSize: 14 }}>
            {t("orBrowseGallery")} →
          </Link>
        ) : null}
        <Typography variant="body2" color="text.secondary">
          {t("canvasDelivery")}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t("canvasUploadQualityHint")}
        </Typography>
      </Stack>
    </Container>
  );
}
